import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";

interface Member {
  id: string;
  email: string;
  nickname: string;
  role: string;
  points: number;
  created_at: string;
}

export default function AdminMembersPage() {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [editRoleUser, setEditRoleUser] = useState<Member | null>(null);
  const [newRole, setNewRole] = useState("");
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchMembers = useCallback(async (query?: string) => {
    setLoading(true);
    try {
      let req = supabase
        .from("profiles")
        .select("id, email, nickname, role, points, created_at", { count: "exact" })
        .order("created_at", { ascending: false })
        .limit(100);

      if (query?.trim()) {
        req = req.or(`nickname.ilike.%${query}%,email.ilike.%${query}%`);
      }

      const { data, count, error } = await req;

      if (error) throw error;
      setMembers(data || []);
      setTotalCount(count || 0);
    } catch {
      setMessage({ type: "error", text: "회원 목록을 불러오지 못했습니다" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      if (searchTimer.current) clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => fetchMembers(query), 300);
    },
    [fetchMembers],
  );

  const handleRoleChange = useCallback(async () => {
    if (!editRoleUser || !newRole) return;

    try {
      const { error } = await supabase
        .from("profiles")
        .update({ role: newRole })
        .eq("id", editRoleUser.id);

      if (error) throw error;

      setMembers((prev) =>
        prev.map((m) => (m.id === editRoleUser.id ? { ...m, role: newRole } : m)),
      );

      setMessage({ type: "success", text: `${editRoleUser.nickname || editRoleUser.email}님의 권한이 "${newRole}"(으)로 변경되었습니다` });
      setEditRoleUser(null);
    } catch {
      setMessage({ type: "error", text: "권한 변경에 실패했습니다" });
    }
  }, [editRoleUser, newRole]);

  const openRoleEditor = useCallback((member: Member) => {
    setEditRoleUser(member);
    setNewRole(member.role);
    setMessage(null);
  }, []);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-foreground-800">회원 목록</h2>
          <p className="text-xs text-foreground-600 mt-0.5">총 {totalCount}명</p>
        </div>

        {/* Search */}
        <div className="relative w-full sm:w-64">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-600">
            <i className="ri-search-line text-sm" />
          </span>
          <input
            type="text"
            placeholder="닉네임 또는 이메일 검색"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full bg-background-100 border border-background-300 rounded-lg pl-9 pr-4 py-2 text-sm text-foreground-700 placeholder-foreground-500 outline-none focus:border-primary-400 transition-colors"
          />
        </div>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`px-4 py-3 rounded-lg text-xs font-medium ${
            message.type === "success"
              ? "bg-accent-100/80 text-accent-800 border border-accent-300/60"
              : "bg-red-500/10 text-red-400 border border-red-500/30"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Role edit modal */}
      {editRoleUser && (
        <div className="bg-background-100 rounded-xl border border-background-300 p-5">
          <h3 className="text-sm font-bold text-foreground-800 mb-3">
            {editRoleUser.nickname || editRoleUser.email}님 권한 변경
          </h3>
          <div className="flex items-center gap-3">
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              className="bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none cursor-pointer"
            >
              <option value="user">user (일반)</option>
              <option value="admin">admin (관리자)</option>
            </select>
            <button
              onClick={handleRoleChange}
              className="px-4 py-2.5 bg-primary-500 text-foreground-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              변경
            </button>
            <button
              onClick={() => setEditRoleUser(null)}
              className="px-4 py-2.5 bg-background-200 text-foreground-600 text-sm font-medium rounded-lg hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
            >
              취소
            </button>
          </div>
        </div>
      )}

      {/* Members Table */}
      <div className="bg-background-100 rounded-xl border border-background-300 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-user-search-line text-xl text-foreground-600" />
            </div>
            <p className="text-sm text-foreground-600">회원이 없습니다</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-300 bg-background-200/50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">닉네임</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">이메일</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">권한</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">포인트</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap hidden md:table-cell">가입일</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">관리</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr
                    key={member.id}
                    className="border-b border-background-300 last:border-0 hover:bg-background-200/40 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-primary-500/15 flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-fill text-primary-400 text-xs" />
                        </div>
                        <span className="text-xs font-medium text-foreground-700 whitespace-nowrap">
                          {member.nickname || "익명"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-foreground-600">{member.email}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
                          member.role === "admin"
                            ? "bg-accent-100/80 text-accent-800"
                            : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {member.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs font-bold text-accent-500 whitespace-nowrap">
                        {member.points.toLocaleString()}P
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-[10px] text-foreground-600 whitespace-nowrap">
                        {new Date(member.created_at).toLocaleDateString("ko-KR")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => openRoleEditor(member)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-medium text-foreground-600 bg-background-200 rounded-md hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-edit-line" />
                        </span>
                        권한
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}